import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import { Input } from '@ooh/ui/components/input';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { RadioGroup, RadioGroupItem } from '@ooh/ui/components/radio-group';

export interface RoleGroupOption {
  id: string;
  /** Translated group label (e.g. "Kierownicy działów"). */
  label: string;
}

/** Derives a `role_permission_scopes`-safe code slug from a display name. */
function slugifyRoleCode(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function isCreateDisabled(
  name: string,
  groupId: string,
  codeTaken: boolean,
  isSubmitting: boolean,
): boolean {
  return name.trim().length === 0 || groupId === '' || codeTaken || isSubmitting;
}

export interface NewRoleDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called with the trimmed name, a generated code, and the chosen group id.
   *
   * NOTE: role groups/tiers are currently a UI-only sidebar-grouping
   * construct — the real `Role` model (see `@ooh/contracts`) has no backing
   * field for it yet. This dialog is intentionally a separate component from
   * `create-role-dialog.tsx` (RHF + Zod + real mutation) because of this
   * extra field; consolidate once the backend gains a role-group column.
   */
  onCreate: (name: string, code: string, groupId: string) => void;
  groups: RoleGroupOption[];
  /** Returns true if `code` already belongs to an existing role — blocks submission to avoid code-keyed collisions. */
  isCodeTaken: (code: string) => boolean;
  /** True while the create-role POST request is in flight. */
  isSubmitting?: boolean;
}

/**
 * Dialog for creating a new role, including a required "Grupa" (group/tier)
 * selector so the new role lands in the correct sidebar section instead of
 * always defaulting silently to one group.
 */
export function NewRoleDialog({
  open,
  onClose,
  onCreate,
  groups,
  isCodeTaken,
  isSubmitting = false,
}: NewRoleDialogProps) {
  const { t } = useTranslation('admin');
  const [name, setName] = useState('');
  const [groupId, setGroupId] = useState(groups[0]?.id ?? '');

  if (!open) return null;

  const code = slugifyRoleCode(name);
  const codeTaken = code !== '' && isCodeTaken(code);

  return (
    <ModalDialog
      header={<h2 className="text-[15px] font-semibold">{t('permissionMatrix.newRole.title')}</h2>}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('permissionMatrix.newRole.cancel')}
          </Button>
          <Button
            disabled={isCreateDisabled(name, groupId, codeTaken, isSubmitting)}
            onClick={() => {
              onCreate(name.trim(), code || `rola_${Date.now()}`, groupId);
              setName('');
              setGroupId(groups[0]?.id ?? '');
            }}
          >
            {isSubmitting
              ? t('permissionMatrix.newRole.creating')
              : t('permissionMatrix.newRole.confirm')}
          </Button>
        </>
      }
    >
      <label className="block text-[13px] font-medium mb-1.5" htmlFor="new-role-name">
        {t('permissionMatrix.newRole.nameLabel')}
      </label>
      <Input
        id="new-role-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('permissionMatrix.newRole.namePlaceholder')}
        autoFocus
      />
      {code ? (
        <p className="mt-1.5 text-2xs font-mono text-muted-foreground">
          {t('permissionMatrix.newRole.codePreview', { code })}
        </p>
      ) : null}
      {codeTaken ? (
        <p className="mt-1 text-2xs text-destructive">{t('permissionMatrix.newRole.codeTaken')}</p>
      ) : null}

      <div className="mt-4">
        <label className="block text-[13px] font-medium mb-1.5">
          {t('permissionMatrix.newRole.groupLabel')}
        </label>
        <RadioGroup value={groupId} onValueChange={setGroupId}>
          {groups.map((group) => (
            <RadioGroupItem key={group.id} value={group.id} label={group.label} />
          ))}
        </RadioGroup>
      </div>
    </ModalDialog>
  );
}
